<template>
  <v-container id="app-scan-nfc-offline">
    <screen
      v-if="amount > 0"
      use-amount
      class="ma-1"
    />

    <scan-progress
      class="my-8"
      :state="scanState"
    />

    <!-- **********************************
    ********** BOUTON LYFPAY **********
    *********************************** -->
    <payment-btn
      v-if="showLyfpay"
      :title="$t('button.scan_lyfpay')"
      color="red"
      logo="lyfpay"
      class="my-2"
      @click="toLyfpay"
    />

    <!-- ***********************
    ********** DECORD **********
    ************************ -->
    <v-icon
      v-if="retryMode"
      large
      color="red"
      style="position:absolute;right:0;opacity:.5;z-index:99"
    >
      {{ IconEnum.refresh }}
    </v-icon>
    <div class="d-flex my-5">
      <img
        :src="AppImages.nfcWave"
        alt="nfc_logo"
        class="ma-auto"
        height="200"
      >
    </div>

    <p class="text-center title">
      {{ $t('field.scan.thx_presenting') }}<br>
      {{ action === 0 ? $t('field.scan.ur_badge') : $t('field.scan.ur_payment') }}
    </p>

    <bottom-action
      btn-valid-disabled
      :btn-del="action < 30"
      :btn-cancel="showCancel"
      :use-listener="false"
      @del="del"
      @cancel="cancel"
    />
  </v-container>
</template>

<script>
import { MetierLocalStorage } from 'modulelocalstorage'

import { ActionEnum, IconEnum } from '@/modules/Enums'
import { AppImages } from '@/modules/Images'
import { Scan } from '@/modules/Scan'
import { $alert, $amount } from '@/modules/Utils'
import ScanProgress from '@/components/scan/Progress'

export default {
  name: 'ScanNfcOfflineView',

  components: {
    ScanProgress
  },

  props: {
    idBadge: { type: String, default: null },
    uid: { type: String, default: null },
    action: { type: Number, default: null },
    mode: { type: String, default: 'P' },
    reference: { type: String, default: null },
    backToHome: { type: Boolean, default: false },
    refillAmount: { type: Number, default: null }
  },

  data () {
    return {
      AppImages,
      IconEnum,
      beep: null,
      //
      offlineData: null,
      retryMode: false,
      lockBack: false,
      scanState: 0
    }
  },

  computed: {
    showCancel () {
      return (this.action === ActionEnum.cashlessReset && this.amount > 0) ||
        this.action !== ActionEnum.badgeInformations
    },
    amount () { return this.$store.state.amount },
    lyfpay () { return this.$store.state.teleparameters.LyfPay },
    isConnected () { return this.$store.getters.isConnected },
    useOnlineRefill () { return this.$store.getters.useOnlineRefill },
    showLyfpay () {
      return this.action === ActionEnum.cashlessDebit &&
        this.lyfpay.Integre === 1 &&
        this.lyfpay.Debit === 1
    },
  },

  created () {
    if (this.action === null) {
      this.$router.replace('/')
    } else {
      this.beep = new Audio(require('@/assets/beep.mp3'))
      this.beep.volume = 1
      this.beep.loop = true
    }
  },

  mounted () {
    this.offlineData = {}
    // Préparation de l'action à mener
    switch (this.action) {
      case ActionEnum.badgeInformations:
        this.offlineData.action = 'transac'
        break
      case ActionEnum.cashlessDebit:
        this.offlineData.action = 'debit'
        this.offlineData.products = JSON.stringify(Scan.formatProductList())
        break
      case ActionEnum.cashlessCredit:
        this.offlineData.action = 'credit'
        break
      case ActionEnum.cashlessRefill:
        this.offlineData.action = 'refill'
        this.offlineData.refillAmount = this.refillAmount
        this.lockBack = true
        break
      case ActionEnum.cancelActivationOffline:
        this.offlineData.action = 'cancel_frais'
        break
      case ActionEnum.cancelDebitOffline:
        this.offlineData.action = 'cancel_debit'
        break
      case ActionEnum.cancelCreditOffline:
        this.offlineData.action = 'cancel_credit'
        break
    }

    this.offlineData.amount = this.amount
    this.offlineData.mode = this.mode
    this.offlineData.uid = this.uid

    // Démarrage de l'écoute nfc
    this.startNfcOffline()
  },

  beforeDestroy () {
    // Arrêt du nfc
    Cashless.destroy()
  },

  methods: {
    startNfcOffline () {
      Cashless.initialize(
        {
          ...this.offlineData,
          isPax: IS_PAX
        },
        (progress) => {
          this.scanState = progress
          if (progress === 1) {
            $alert(false)
            this.beep.play()
          } else if (progress > 1) {
            this.beep.pause()
          }
        },
        (result) => {
          setTimeout(() => {
            this.scanState = 3
          }, 200)

          // Fermeture d'une éventuelle alerte
          $alert(false)
          // Vibration de succès
          navigator.vibrate(300)

          setTimeout(() => {
            this.onScanSuccess(result)
          }, 800)
        },
        (error) => {
          // Vibration d'erreur
          navigator.vibrate(700)
          // Consultation de l'erreur
          this.onScanError(error)
        }
      )
    },
    onScanSuccess (result) {
      if (this.action === ActionEnum.badgeInformations) {
        // Redirection vers Info Badge (offline)
        this.$router.replace({ name: 'info-badge-offline', params: { infos: result } })
      } else if (this.action === ActionEnum.cancelActivationOffline) {
        // Pas de data dans success -> +2 transactions + message + home
        this.$store.dispatch('addCashlessTransaction')
        this.$store.dispatch('addCashlessTransaction')

        $alert(this.$i18n.t('success.offline_cancel_activation'), 'success')
        this.$router.replace('home')
      } else {
        this.$store.dispatch('addCashlessTransaction')

        // Sinon on formate et affiche le ticket
        let tickets = []

        switch (this.action) {
          case ActionEnum.cashlessRefill:
            // Si frais -> +1 transaction
            if (result[0].Frais !== 0) {
              this.$store.dispatch('addCashlessTransaction', result[0].Frais)
            }
            tickets.push(Scan.formatTicket(
              this.$i18n.t('tickets.reload'),
              this.refillAmount,
              result[0].SoldeBadge - this.refillAmount,
              result[0].SoldeBadge,
              result[0].Frais,
              result[0].Code
            ))

            if (this.amount > 0 && result[1]) {
              tickets.push(Scan.formatTicket(
                this.$i18n.t('tickets.payment'),
                this.amount,
                result[1].SoldeBadge + this.amount,
                result[1].SoldeBadge
              ))

              this.$store.dispatch('addCashlessTransaction')
            }
            break
          case ActionEnum.cashlessCredit:
            // Si frais -> +1 transaction
            if (result.Frais !== 0) {
              this.$store.dispatch('addCashlessTransaction', result.Frais)
            }
            tickets.push(Scan.formatTicket(
              this.$i18n.t('tickets.reload'),
              this.amount,
              result.SoldeBadge - this.amount,
              result.SoldeBadge,
              result.Frais,
              result.Code
            ))
            break
          case ActionEnum.cashlessDebit:
            tickets.push(Scan.formatTicket(
              this.$i18n.t('tickets.payment'),
              this.amount,
              result.SoldeBadge + this.amount,
              result.SoldeBadge
            ))
            break
          case ActionEnum.cancelCreditOffline:
            tickets.push(Scan.formatTicket(
              this.$i18n.t('tickets.cancel_reload'),
              this.amount,
              result.SoldeBadge + this.amount,
              result.SoldeBadge
            ))
            break
          case ActionEnum.cancelDebitOffline:
            tickets.push(Scan.formatTicket(
              this.$i18n.t('tickets.cancel_payment'),
              this.amount,
              result.SoldeBadge - this.amount,
              result.SoldeBadge
            ))
            break
          default:
            break
        }

        // Enregistrement du ticket + Redirection vers ticket
        MetierLocalStorage.setTicket(tickets)
        this.$router.replace({ name: 'ticket', params: {
            tickets: tickets,
            solde: $amount(result[1] ? result[1].SoldeBadge : (result[0] ? result[0].SoldeBadge : result.SoldeBadge))
            // startPrint: (result.Frais !== 0)
          } })
      }
    },
    onScanError (result) {
      if (result.code === 100) {
        // Solde insuffisant
        if (this.isConnected && this.useOnlineRefill) {
          $alert(result.error, 'error', '', -1, [{
            title: this.$t('field.scan.reload_online'),
            color: 'success',
            click: () => { Scan.checkRefill(result.uid, null, result.solde, this.amount) }
          }])
        } else {
          $alert(this.$i18n.t('error.scan_retry'))
        }
      } else if (result.code === 10) {
        // Info with msg
        $alert(result.error, 'info')
      } else if (result.code === 11) {
        // Error with msg
        $alert(result.error)
      } else if (result.code === 12) {
        // Erreur de lecture
        // $alert(this.$i18n.t('info.offline_read_problem'), 'info')
        $alert(result.error)
      } else if (result.code === 13) {
        // Erreur d'écriture
        $alert(this.$i18n.t('error.offline_write'))
      } else if (result.code === 14) {
        // Erreur d'écriture de transaction
        this.retryMode = true
        this.offlineData = {
          action: 'retry',
          mode: this.mode
        }

        $alert(this.$i18n.t('info.offline_write_trx_problem_rescan'), 'warningred', '', 60000, [{
            title: 'Ok, je rescanne',
            color: 'error',
          }], null, false)
      } else {
        // Autre erreur
        if (result.error) {
          $alert(this.$i18n.t('error.scan_retry'))
        } else {
          $alert(result)
          this.$router.replace('/home')
          return
        }
      }

      this.resetNfc()
    },
    resetNfc () {
      // Stop NFC
      Cashless.destroy()
      // Start NFC after 1 seconde
      setTimeout(this.startNfcOffline, 2000)
    },
    cancelRefill (idbadge) {
      Scan.cancelDebitOnline(
        idbadge,
        () => {
          $alert(this.$i18n.t('success.offline_cancel_credit'), 'success')

          this.$router.replace('/home')
        },
        (error) => {
          $alert(error)
        }
      )
    },
    // **********************************************************************
    // **********************************************************************
    del () {
      if (this.backToHome) {
        this.$router.replace({ name: 'home', params: { reset: false } })
      } else {
        this.$router.replace('/action-choice')
      }
    },
    cancel () {
      // Si on est sur un refill, on annule celui-ci
      if (this.lockBack) {
        this.cancelRefill(this.idBadge)
      } else {
        this.$router.replace('/home')
      }
    },
    toLyfpay () {
      this.$emit('title', this.$i18n.t('button.lyfpay.debit_display'))
      this.$router.replace({
        name: 'scan-qrcode',
        params: { action: ActionEnum.lyfpayDebit, mode: 'L', backToNfcOffline: true }
      })
    }
  }
}
</script>
